import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CatalogProduct,
  CatalogProductBulkImportBodyParams,
  CatalogProductBulkImportResponse,
  CatalogProductCreateBodyParams,
  CatalogProductUpdateBodyParams,
  CatalogProductsService,
} from 'src/api/generated';

/**
 * Options for configuring the useProductImport hook behavior.
 * Allows customization of success and error callback handlers.
 */
interface UseProductImportOptions {
  /** Callback invoked when the product import succeeds. Receives the import response. */
  onSuccess?: (response: CatalogProductBulkImportResponse) => void;
  /** Callback invoked when the product import fails. Receives the error object. */
  onError?: (error: Error) => void;
}

/**
 * Response object returned by the useProductImport hook.
 * Contains the mutation function, loading state, and error information.
 */
interface UseProductImportResponse {
  /** Function to trigger the product import mutation with the provided data. */
  importProducts: (
    body: CatalogProductBulkImportBodyParams
  ) => Promise<CatalogProductBulkImportResponse>;
  /** Whether the import operation is currently in progress. */
  loading: boolean;
  /** Error object if the import failed, or null if no error occurred. */
  error: Error | null;
}

/**
 * Hook for bulk importing catalog products.
 * Handles product import requests and automatically invalidates related cache on success.
 *
 * @param options - Configuration options including success and error callbacks
 * @returns Response object containing the mutation function, loading state, and error information
 *
 * @example
 * const { importProducts, loading, error } = useProductImport({
 *   onSuccess: (response) => console.log('Imported:', response.count),
 *   onError: (error) => console.error('Import failed:', error),
 * });
 * await importProducts({ file: data });
 */
export const useProductImport = (
  options: UseProductImportOptions = {}
): UseProductImportResponse => {
  const queryClient = useQueryClient();
  const { onSuccess = () => {}, onError = () => {} } = options;

  const importProductsRequest = async (
    body: CatalogProductBulkImportBodyParams
  ): Promise<CatalogProductBulkImportResponse> => {
    return await CatalogProductsService.importProducts(body);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<
    CatalogProductBulkImportResponse,
    Error,
    CatalogProductBulkImportBodyParams
  >({
    mutationKey: ['catalog/products/import'],
    mutationFn: importProductsRequest,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['catalog/products'] });
      onSuccess(response);
    },
    onError,
  });

  return {
    importProducts: mutateAsync,
    loading,
    error: error ?? null,
  };
};

/**
 * Options for configuring product mutation hook behavior.
 * Allows customization of success and error callback handlers.
 */
interface UseProductMutationOptions {
  /** Callback invoked when the product mutation succeeds. Receives the product object. */
  onSuccess?: (product: CatalogProduct) => void;
  /** Callback invoked when the product mutation fails. Receives the error object. */
  onError?: (error: Error) => void;
}

/**
 * Response object returned by product mutation hooks.
 * Contains loading state and error information.
 */
interface UseProductMutationResponse {
  /** Whether the mutation operation is currently in progress. */
  loading: boolean;
  /** Error object if the mutation failed, or null if no error occurred. */
  error: Error | null;
}

/**
 * Hook for creating a new catalog product.
 * Handles product creation requests and automatically invalidates related cache on success.
 *
 * @param options - Configuration options including success and error callbacks
 * @returns Response object containing the mutation function, loading state, and error information
 *
 * @example
 * const { createProduct, loading, error } = useCreateProduct({
 *   onSuccess: (product) => console.log('Created:', product.id),
 *   onError: (error) => console.error('Creation failed:', error),
 * });
 * await createProduct({ name: 'New Product', price: 99.99 });
 */
export const useCreateProduct = (
  options: UseProductMutationOptions = {}
): UseProductMutationResponse & {
  createProduct: (
    data: CatalogProductCreateBodyParams
  ) => Promise<CatalogProduct>;
} => {
  const queryClient = useQueryClient();
  const { onSuccess = () => {}, onError = () => {} } = options;

  const createProductRequest = async (
    data: CatalogProductCreateBodyParams
  ): Promise<CatalogProduct> => {
    return await CatalogProductsService.createProduct(data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<CatalogProduct, Error, CatalogProductCreateBodyParams>({
    mutationKey: ['catalog/products/create'],
    mutationFn: createProductRequest,
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: ['catalog/products'] });
      onSuccess(product);
    },
    onError,
  });

  return {
    createProduct: mutateAsync,
    loading,
    error: error ?? null,
  };
};

/**
 * Hook for updating an existing catalog product.
 * Handles product update requests and automatically invalidates related cache on success.
 *
 * @param options - Configuration options including success and error callbacks
 * @returns Response object containing the mutation function, loading state, and error information
 *
 * @example
 * const { updateProduct, loading, error } = useUpdateProduct({
 *   onSuccess: (product) => console.log('Updated:', product.id),
 *   onError: (error) => console.error('Update failed:', error),
 * });
 * await updateProduct({ id: 1, name: 'Updated Product' });
 */
export const useUpdateProduct = (
  options: UseProductMutationOptions = {}
): UseProductMutationResponse & {
  updateProduct: (
    params: { id: number } & CatalogProductUpdateBodyParams
  ) => Promise<CatalogProduct>;
} => {
  const queryClient = useQueryClient();
  const { onSuccess = () => {}, onError = () => {} } = options;

  const updateProductRequest = async ({
    id,
    ...data
  }: {
    id: number;
  } & CatalogProductUpdateBodyParams): Promise<CatalogProduct> => {
    return await CatalogProductsService.updateProduct(id, data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<
    CatalogProduct,
    Error,
    { id: number } & CatalogProductUpdateBodyParams
  >({
    mutationKey: ['catalog/products/update'],
    mutationFn: updateProductRequest,
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: ['catalog/products'] });
      queryClient.invalidateQueries({
        queryKey: ['catalog/products', 'detail', product.id],
      });
      onSuccess(product);
    },
    onError,
  });

  return {
    updateProduct: mutateAsync,
    loading,
    error: error ?? null,
  };
};

/**
 * Hook for deleting a catalog product.
 * Handles product deletion requests and automatically invalidates related cache on success.
 *
 * @param options - Configuration options including success and error callbacks
 * @returns Response object containing the mutation function, loading state, and error information
 *
 * @example
 * const { deleteProduct, loading, error } = useDeleteProduct({
 *   onSuccess: (product) => console.log('Deleted:', product.id),
 *   onError: (error) => console.error('Deletion failed:', error),
 * });
 * await deleteProduct(1);
 */
export const useDeleteProduct = (
  options: UseProductMutationOptions = {}
): UseProductMutationResponse & {
  deleteProduct: (id: number) => Promise<CatalogProduct>;
} => {
  const queryClient = useQueryClient();
  const { onSuccess = () => {}, onError = () => {} } = options;

  const deleteProductRequest = async (id: number): Promise<CatalogProduct> => {
    return await CatalogProductsService.deleteProduct(id);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation<CatalogProduct, Error, number>({
    mutationKey: ['catalog/products/delete'],
    mutationFn: deleteProductRequest,
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: ['catalog/products'] });
      queryClient.invalidateQueries({
        queryKey: ['catalog/products', 'detail', product.id],
      });
      onSuccess(product);
    },
    onError,
  });

  return {
    deleteProduct: mutateAsync,
    loading,
    error: error ?? null,
  };
};
